/**
 * AppNav — sidebar navigation items for the authenticated shell.
 *
 * The item list is config-driven via APP_CONFIG.navigation (config/app.ts).
 * Today's items:
 *   - Dashboard (/dashboard)        — always visible
 *   - Submissions (/submissions)    — always visible
 *   - Intake Form (/intake-form)    — visible to reviewer or admin
 *   - Admin (/admin)                — visible to admin only
 *
 * Each NavItem uses an href so ctrl/cmd+click opens a new tab.
 * The parent NavDrawer (in AuthenticatedLayout) intercepts plain left-clicks
 * via onNavItemSelect for SPA navigation.
 *
 * Icon pairs: regular (resting) and filled (selected), keyed by the config
 * `icon` string, matching the reference app's Navigation.tsx pattern.
 */

import type { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { NavItem } from '@fluentui/react-nav';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
  Board20Regular,
  Board20Filled,
  ClipboardTaskListLtr20Regular,
  ClipboardTaskListLtr20Filled,
  DocumentBulletList20Regular,
  DocumentBulletList20Filled,
  Settings20Regular,
  Settings20Filled,
} from '@fluentui/react-icons';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { APP_CONFIG } from '@/config/app';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  navItem: {
    backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusXLarge,
    ':hover': { backgroundColor: tokens.colorNeutralBackground4Hover },
    '&[aria-current="page"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-current="page"]:hover': {
      backgroundColor: tokens.colorNeutralBackground4Hover,
    },
    '&[aria-current="page"] .fui-NavItem__icon': {
      color: tokens.colorBrandBackground,
    },
  },
});

// ---------------------------------------------------------------------------
// Icon map — resolves APP_CONFIG.navigation `icon` keys to regular/filled pairs.
// ---------------------------------------------------------------------------

const ICONS: Record<string, { regular: ReactElement; filled: ReactElement }> = {
  dashboard: { regular: <Board20Regular />, filled: <Board20Filled /> },
  submissions: { regular: <ClipboardTaskListLtr20Regular />, filled: <ClipboardTaskListLtr20Filled /> },
  intakeForm: { regular: <DocumentBulletList20Regular />, filled: <DocumentBulletList20Filled /> },
  admin: { regular: <Settings20Regular />, filled: <Settings20Filled /> },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppNav() {
  const styles = useStyles();
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const isAdmin = user?.role === 'admin';
  const isReviewerOrAdmin = user?.role === 'reviewer' || user?.role === 'admin';

  const visibleItems = APP_CONFIG.navigation.filter((item) => {
    if (item.requires === 'admin') return isAdmin;
    if (item.requires === 'reviewer') return isReviewerOrAdmin;
    return true;
  });

  return (
    <>
      {visibleItems.map((item) => {
        const selected = pathname === item.path || pathname.startsWith(item.path + '/');
        const iconSet = ICONS[item.icon];
        const icon = iconSet ? (selected ? iconSet.filled : iconSet.regular) : undefined;
        return (
          <NavItem
            key={item.key}
            value={item.path}
            href={item.path}
            icon={icon}
            className={styles.navItem}
          >
            {item.label}
          </NavItem>
        );
      })}
    </>
  );
}

export default AppNav;
